import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  listarIntegracoes,
  obterStatusFila,
  listarEventos,
  conectarIntegracao,
  desconectarIntegracao,
  retentarEvento,
  enfileirarEventoMock,
} from '../../../services/integracaoService';

// ============================================================
// Slice de integracoes externas (T1 / S2).
// Estado:
//   - lista: integracoes do tenant (do backend)
//   - statusFila: estatistica do worker
//   - eventosPorIntegracao: { [idIntegracao]: [eventos...] }
// ============================================================

const initialState = {
  lista: [],
  statusFila: null,
  eventosPorIntegracao: {},
  loadingLista: false,
  loadingFila: false,
  loadingEventosId: null, // id da integracao com fetch de eventos em andamento
  error: null,
};

export const fetchIntegracoes = createAsyncThunk(
  'integracoes/fetchLista',
  async () => listarIntegracoes(),
);

export const fetchStatusFila = createAsyncThunk(
  'integracoes/fetchStatusFila',
  async () => obterStatusFila(),
);

export const fetchEventos = createAsyncThunk(
  'integracoes/fetchEventos',
  async ({ idIntegracao, limit = 50 }) => {
    const eventos = await listarEventos(idIntegracao, limit);
    return { idIntegracao, eventos };
  },
);

export const conectarIntegracaoThunk = createAsyncThunk(
  'integracoes/conectar',
  async (tipo, { dispatch }) => {
    const resp = await conectarIntegracao(tipo);
    // Recarrega lista para refletir novo registro.
    dispatch(fetchIntegracoes());
    return resp;
  },
);

export const desconectarIntegracaoThunk = createAsyncThunk(
  'integracoes/desconectar',
  async (id, { dispatch }) => {
    const resp = await desconectarIntegracao(id);
    dispatch(fetchIntegracoes());
    return resp;
  },
);

export const retentarEventoThunk = createAsyncThunk(
  'integracoes/retentarEvento',
  async ({ idIntegracao, idEvento }, { dispatch }) => {
    const resp = await retentarEvento(idIntegracao, idEvento);
    dispatch(fetchEventos({ idIntegracao }));
    return resp;
  },
);

export const enfileirarMockThunk = createAsyncThunk(
  'integracoes/enfileirarMock',
  async ({ tipoEvento, payload }, { dispatch }) => {
    const resp = await enfileirarEventoMock(tipoEvento, payload);
    if (resp.idintegracao) {
      // Refresca eventos do registro mock para o usuario ver imediatamente.
      setTimeout(() => dispatch(fetchEventos({ idIntegracao: resp.idintegracao })), 800);
    }
    dispatch(fetchIntegracoes());
    return resp;
  },
);

const IntegracoesSlice = createSlice({
  name: 'integracoes',
  initialState,
  reducers: {
    limparErro: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchIntegracoes.pending, (state) => {
        state.loadingLista = true;
        state.error = null;
      })
      .addCase(fetchIntegracoes.fulfilled, (state, action) => {
        state.loadingLista = false;
        state.lista = action.payload || [];
      })
      .addCase(fetchIntegracoes.rejected, (state, action) => {
        state.loadingLista = false;
        state.error = action.error?.message || 'Falha ao carregar integracoes';
      })

      .addCase(fetchStatusFila.pending, (state) => {
        state.loadingFila = true;
      })
      .addCase(fetchStatusFila.fulfilled, (state, action) => {
        state.loadingFila = false;
        state.statusFila = action.payload;
      })
      .addCase(fetchStatusFila.rejected, (state, action) => {
        state.loadingFila = false;
        state.error = action.error?.message || 'Falha ao carregar status do worker';
      })

      .addCase(fetchEventos.pending, (state, action) => {
        state.loadingEventosId = action.meta.arg.idIntegracao;
      })
      .addCase(fetchEventos.fulfilled, (state, action) => {
        state.loadingEventosId = null;
        state.eventosPorIntegracao[action.payload.idIntegracao] = action.payload.eventos || [];
      })
      .addCase(fetchEventos.rejected, (state, action) => {
        state.loadingEventosId = null;
        state.error = action.error?.message || 'Falha ao carregar eventos';
      });
  },
});

export const { limparErro } = IntegracoesSlice.actions;
export default IntegracoesSlice.reducer;
